import { parseU64 } from "@bridgesplit/utils";

import { EarnVaultWithLatestSnapshotApi } from "../api/earn";
import {
    ExponentStrategyFinancialsApi,
    ExponentStrategyProposalVoteConfigApi,
    ExponentStrategyReservesConfigApi,
    ExponentStrategyRolesApi,
    ExponentStrategyWithdrawalPeriodApi,
    ExponentStrategyVaultApi,
    ParsedStrategyAccountJsonApi
} from "../api/earn-strategy";
import {
    EarnExponentAccount,
    EarnExponentAuthorities,
    EarnExponentFinancials,
    EarnExponentProposalVoteConfig,
    EarnExponentReservesConfig,
    EarnExponentWithdrawalPeriod,
    EarnProviderKind,
    ParsedEarnAccount
} from "../domain/earn";

function parseAuthorities(api: ExponentStrategyRolesApi | undefined): EarnExponentAuthorities {
    if (!api) return {};
    return {
        curator: api.curator?.[0] || undefined,
        manager: api.manager?.[0] || undefined,
        sentinel: api.sentinel?.[0] || undefined,
        allocator: api.allocator?.[0] || undefined
    };
}

function parseFinancials(api: ExponentStrategyFinancialsApi | undefined): EarnExponentFinancials | undefined {
    if (!api) return undefined;
    const cap = parseU64(api.instantWithdrawalWindowCapAum);
    const used = parseU64(api.instantWithdrawnAumInWindow);
    const end = parseU64(api.instantWithdrawalWindowEnd);
    const aum = parseU64(api.aumInBase);
    const positionsAum = parseU64(api.aumInBaseInPositions);
    const pendingLp = parseU64(api.totalLpPendingWithdrawals);
    if (cap == null && used == null && end == null && aum == null && positionsAum == null) return undefined;
    return {
        instantWithdrawalWindowCapAum: cap ?? 0n,
        instantWithdrawnAumInWindow: used ?? 0n,
        instantWithdrawalWindowEnd: end ?? 0n,
        aumInBase: aum ?? 0n,
        aumInBaseInPositions: positionsAum ?? 0n,
        totalLpPendingWithdrawals: pendingLp ?? 0n
    };
}

function parseWithdrawalPeriod(
    api: ExponentStrategyWithdrawalPeriodApi | undefined
): EarnExponentWithdrawalPeriod | undefined {
    if (api === undefined) return undefined;
    if (typeof api === "number") return api;
    return {
        kind: api.kind,
        daysOfWeek: api.daysOfWeek ?? api.days_of_week ?? 0,
        weekInterval: api.weekInterval ?? api.week_interval ?? 1,
        referenceTimestamp: api.referenceTimestamp ?? api.reference_timestamp ?? 0
    };
}

function parseOptionalNumber(api: number | string | undefined): number | undefined {
    if (api === undefined) return undefined;
    const value = typeof api === "number" ? api : Number(api);
    return Number.isFinite(value) ? value : undefined;
}

function parseReservesConfig(
    api: ExponentStrategyReservesConfigApi | undefined
): EarnExponentReservesConfig | undefined {
    if (!api) return undefined;
    return {
        reservesShareBp: parseOptionalNumber(api.reservesShareBp),
        managementFeeBps: parseOptionalNumber(api.managementFeeBps),
        fastWithdrawalCutBp: parseOptionalNumber(api.fastWithdrawalCutBp),
        withdrawalPeriod: parseWithdrawalPeriod(api.withdrawalPeriod),
        instantWithdrawalWindowLimitBp: parseOptionalNumber(api.instantWithdrawalWindowLimitBp),
        withdrawalCancelCooldownSeconds: parseOptionalNumber(api.withdrawalCancelCooldownSeconds)
    };
}

function parseProposalVoteConfig(
    api: ExponentStrategyProposalVoteConfigApi | undefined
): EarnExponentProposalVoteConfig | undefined {
    if (!api) return undefined;
    return {
        rejectionThresholdBps: parseOptionalNumber(api.rejectionThresholdBps),
        defaultTimelockSeconds: parseOptionalNumber(api.defaultTimelockSeconds),
        defaultVotingPeriodSeconds: parseOptionalNumber(api.defaultVotingPeriodSeconds)
    };
}

function parseExponentStrategyAccount(api: ExponentStrategyVaultApi): EarnExponentAccount {
    return {
        squadsVault: api.squadsVault || undefined,
        tokenEntries: (api.tokenEntries ?? []).flatMap((entry) =>
            entry.mint ? [{ mint: entry.mint, reserve: parseU64(entry.lastObservedAmount) ?? 0n }] : []
        ),
        destinationTokenAccounts:
            api.strategyPositions
                ?.flatMap((position) => position.value?.balances?.map((balance) => balance.tokenAccount) ?? [])
                .filter((tokenAccount): tokenAccount is string => !!tokenAccount) ?? [],
        authorities: parseAuthorities(api.roles),
        maxAumSupply: parseU64(api.maxAumSupply) ?? undefined,
        normalWithdrawalCutBp: parseOptionalNumber(api.normalWithdrawalCutBp),
        financials: parseFinancials(api.financials),
        reservesConfig: parseReservesConfig(api.reservesConfig),
        proposalVoteConfig: parseProposalVoteConfig(api.proposalVoteConfig)
    };
}

/**
 * Codec for `parsedAccountJson` keyed off the vault's `providerKind`.
 *
 * v1 only decodes Exponent. Glam / Upshift / LoopscaleVault carry their own on-chain shapes
 * that the BE serializes through a different `serde_json::Value` branch — until those shapes
 * are finalized the corresponding umbrella key stays `undefined` so the UI falls back to the
 * "data unavailable" / unsupported-provider path.
 *
 * Defensive: never throws. A bad/missing JSON yields an empty umbrella (all keys undefined).
 */
export function parseParsedEarnAccountFromApi(api: EarnVaultWithLatestSnapshotApi): ParsedEarnAccount {
    const json = api.parsedAccountJson as ParsedStrategyAccountJsonApi | undefined | null;
    const exponent =
        api.providerKind === EarnProviderKind.Exponent && json?.exponentStrategyVault
            ? parseExponentStrategyAccount(json.exponentStrategyVault)
            : undefined;
    return { exponent };
}
